'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  NO_STAGE_INSETS,
  combineStageInsets,
  type StageEdge,
  type StageInsetContribution,
  type StageInsets,
} from './CopStageInsets';

/**
 * Who is covering the stage, measured rather than assumed.
 *
 * Every floating host registers itself and reports its own measured size; the
 * registry folds those into one `StageInsets` record that the manual-flight
 * geometry reads. Measured, because the alternative is a table of magic numbers
 * that goes stale the first time a bar gains a line of text - and the number that
 * goes stale decides whether the COP offers manual flight into a surface it
 * cannot draw a control in.
 *
 * A `ResizeObserver` per host and not one on the stage: the stage's own box does
 * not change when a bar appears over it (that is the point of the tactical
 * shell), so there is nothing there to observe.
 */
const StageInsetsContext = createContext<StageInsets>(NO_STAGE_INSETS);

type Register = (id: string, contribution: StageInsetContribution | null) => void;

const StageInsetRegisterContext = createContext<Register | null>(null);

export interface StageInsetsController {
  insets: StageInsets;
  register: Register;
}

/**
 * Holds the registry. Called by the COP page, which needs the value ITSELF -
 * `surfaceControllable` is computed there - and cannot consume a context it
 * renders. So the page owns the state and hands it to the provider below.
 */
export function useStageInsetsController(): StageInsetsController {
  const [contributions, setContributions] = useState<Record<string, StageInsetContribution>>({});

  const register = useCallback<Register>((id, contribution) => {
    setContributions((prev) => {
      const current = prev[id];
      if (contribution === null) {
        if (current === undefined) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      // Bail on an unchanged measurement. A ResizeObserver fires on every layout
      // pass, and a fresh object each time would re-render the whole COP - the
      // controller lives in `CopView` - at animation frame rate for a box that
      // has not moved. Sizes are rounded before they get here, so browser zoom
      // and fractional layout cannot defeat this by a hundredth of a pixel.
      if (current !== undefined
        && current.edge === contribution.edge
        && current.sizePx === contribution.sizePx) {
        return prev;
      }
      return { ...prev, [id]: contribution };
    });
  }, []);

  const insets = useMemo(
    () => combineStageInsets(Object.values(contributions)),
    [contributions],
  );

  return useMemo(() => ({ insets, register }), [insets, register]);
}

export function StageInsetsProvider({
  controller,
  children,
}: {
  controller: StageInsetsController;
  children: ReactNode;
}) {
  return (
    <StageInsetRegisterContext.Provider value={controller.register}>
      <StageInsetsContext.Provider value={controller.insets}>
        {children}
      </StageInsetsContext.Provider>
    </StageInsetRegisterContext.Provider>
  );
}

/**
 * What is covering the stage right now.
 *
 * Outside a provider this is all zeroes, which is the honest answer for the
 * classic shell and for every component rendered in a test without one.
 */
export function useStageInsets(): StageInsets {
  return useContext(StageInsetsContext);
}

/**
 * What is covering the BOX this subtree is drawn in, which is not always the
 * stage.
 *
 * `ManualStage` splits the stage into a map half and a picture half, and in that
 * layout the two halves meet different chrome: the side panel is anchored to the
 * stage's right edge, so it covers the picture and none of the map. A component
 * inside one of those halves cannot work that out for itself - it does not know
 * which half it is in, or that there is a half at all - so the stage says.
 *
 * One rule, one place, which is the same reason `stageInsetsForHost` exists: the
 * box that is MEASURED for the MANUAL verdict and the surfaces that are DRAWN
 * inside it have to agree, or the key and the control surface disagree about
 * whether there is room to fly.
 */
const EnclosingStageInsetsContext = createContext<StageInsets | null>(null);

export function EnclosingStageInsetsProvider({
  insets,
  children,
}: {
  insets: StageInsets;
  children: ReactNode;
}) {
  return (
    <EnclosingStageInsetsContext.Provider value={insets}>
      {children}
    </EnclosingStageInsetsContext.Provider>
  );
}

/**
 * The chrome covering this subtree's box, falling back to the stage's own.
 *
 * The fallback is what keeps every component that is rendered outside a
 * `ManualStage` - in a test, or in some future host - reading the whole stage,
 * which is the correct answer when the box IS the stage.
 */
export function useEnclosingStageInsets(): StageInsets {
  const enclosing = useContext(EnclosingStageInsetsContext);
  const stage = useContext(StageInsetsContext);
  return enclosing ?? stage;
}

/**
 * Attach to a floating host so it declares the edge it covers.
 *
 * `active` is what keeps the register honest across breakpoints. Below `lg` the
 * bottom stack is still in the map's flow rather than over it, so it covers
 * nothing - and reporting its height there would shrink the measured surface
 * twice, once by the layout and once by this.
 *
 * Height for a top or bottom edge, width for a left or right one. The host does
 * not have to know which; it names the edge it is on.
 */
export function useStageInsetHost<T extends HTMLElement>(
  edge: StageEdge,
  active: boolean,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const register = useContext(StageInsetRegisterContext);
  const id = useId();

  useEffect(() => {
    if (register === null) return;
    if (!active) {
      register(id, null);
      return;
    }

    const el = ref.current;
    if (!el) {
      register(id, null);
      return;
    }

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const raw = edge === 'top' || edge === 'bottom' ? rect.height : rect.width;
      // Up, never down: this is the distance a control surface must stay clear
      // of, so a rounding error has to cost a pixel of map rather than put the
      // grab halo a pixel under the command bar.
      register(id, { edge, sizePx: Math.ceil(raw) });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      // A window `resize` is NOT enough on its own here, unlike in
      // `useMeasuredSize`: a bar appearing inside the stack changes this box
      // without changing the window, and a stale inset is the grab halo drawn
      // under the command bar. So the layout effect below re-measures on every
      // render as well, which is what actually keeps this branch honest; the
      // listener only adds the case where nothing re-rendered.
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
        register(id, null);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      register(id, null);
    };
  }, [register, id, edge, active]);

  // Belt and braces for a browser with no `ResizeObserver`, and free everywhere
  // else: `register` bails on an unchanged size, so this cannot loop.
  useLayoutEffect(() => {
    if (register === null || !active) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'undefined') return;
    const rect = el.getBoundingClientRect();
    const raw = edge === 'top' || edge === 'bottom' ? rect.height : rect.width;
    register(id, { edge, sizePx: Math.ceil(raw) });
  });

  return ref;
}
